/**
 * Document Expiration Widget
 * 
 * Displays a summary of expiring documents on the home screen
 */

import { useMemo, useState, useEffect } from 'react';
import { View, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { ThemedText, SubHeading, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import { subscribeToUserDocuments, type Document } from '@/services/documentVault';
import {
  getExpiredDocuments,
  getDocumentsExpiringWithin,
} from '@/utils/documentExpiration';

export function DocumentExpirationWidget() {
  const theme = useTheme();
  const router = useRouter();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [documents, setDocuments] = useState<Document[]>([]);

  // Subscribe to documents
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setDocuments([]);
      return;
    }

    const unsubscribe = subscribeToUserDocuments(firebaseUser.uid, (userDocuments) => {
      setDocuments(userDocuments);
    });

    return () => unsubscribe();
  }, [firebaseUser?.uid]);

  // Calculate expiration status
  const expirationSummary = useMemo(() => {
    if (!documents || documents.length === 0) {
      return null;
    }

    const expired = getExpiredDocuments(documents);
    const expiringVerySoon = getDocumentsExpiringWithin(documents, 30);
    const expiringSoon = getDocumentsExpiringWithin(documents, 90);

    // Filter out documents that are already in expired or expiringVerySoon
    const expiringSoonOnly = expiringSoon.filter(
      (doc) => !expired.includes(doc) && !expiringVerySoon.includes(doc)
    );

    if (expired.length === 0 && expiringVerySoon.length === 0 && expiringSoonOnly.length === 0) {
      return null;
    }

    return {
      expired: expired.length,
      expiringVerySoon: expiringVerySoon.length,
      expiringSoon: expiringSoonOnly.length,
      total: expired.length + expiringVerySoon.length + expiringSoonOnly.length,
    };
  }, [documents]);

  if (!expirationSummary || expirationSummary.total === 0) {
    return null;
  }

  const getPriorityColor = () => {
    if (expirationSummary.expired > 0) return '#FF3B30'; // Red
    if (expirationSummary.expiringVerySoon > 0) return '#FF9500'; // Orange
    return '#FFCC00'; // Yellow
  };

  const getPriorityLabel = () => {
    if (expirationSummary.expired > 0) return 'Action Required';
    if (expirationSummary.expiringVerySoon > 0) return 'Renewal Needed';
    return 'Upcoming Renewals';
  };

  const priorityColor = getPriorityColor();
  const priorityLabel = getPriorityLabel();

  return (
    <Pressable
      onPress={() => router.push('/(tabs)/documents')}
      style={({ pressed }) => ({
        opacity: pressed ? 0.92 : 1,
        transform: [{ scale: pressed ? 0.98 : 1 }],
      })}
    >
      <Card
        variant="elevated"
        elevation={0}
        style={{
          backgroundColor: theme.colors.backgroundSecondary,
          borderWidth: 1,
          borderColor: theme.colors.borderMuted,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.25), marginBottom: theme.spacing(0.75) }}>
          <View
            style={{
              width: 36,
              height: 36,
              borderRadius: theme.radii.pill,
              backgroundColor: priorityColor + '16',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Ionicons
              name={expirationSummary.expired > 0 ? 'alert-circle' : 'time-outline'}
              size={18}
              color={priorityColor}
            />
          </View>
          <View style={{ flex: 1 }}>
            <SubHeading style={{ fontSize: 16, marginBottom: theme.spacing(0.15) }}>
              Document Expiration
            </SubHeading>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                {priorityLabel}
              </Caption>
              <View
                style={{
                  paddingHorizontal: theme.spacing(0.9),
                  paddingVertical: theme.spacing(0.35),
                  borderRadius: theme.radii.pill,
                  backgroundColor: priorityColor + '16',
                }}
              >
                <Caption style={{ fontSize: 11, fontWeight: '700', color: priorityColor }}>
                  {expirationSummary.total} total
                </Caption>
              </View>
            </View>
          </View>
          <Ionicons name="chevron-forward" size={18} color={theme.colors.textSecondary} />
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.25), flexWrap: 'wrap' }}>
          {expirationSummary.expired > 0 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <View
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#FF3B30',
                }}
              />
              <Caption color="#FF3B30" style={{ fontSize: 12, fontWeight: '700' }}>
                {expirationSummary.expired} expired
              </Caption>
            </View>
          )}
          {expirationSummary.expiringVerySoon > 0 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <View
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#FF9500',
                }}
              />
              <Caption color="#FF9500" style={{ fontSize: 12, fontWeight: '700' }}>
                {expirationSummary.expiringVerySoon} in 30 days
              </Caption>
            </View>
          )}
          {expirationSummary.expiringSoon > 0 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <View
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#FFCC00',
                }}
              />
              <Caption color="#FFCC00" style={{ fontSize: 12, fontWeight: '700' }}>
                {expirationSummary.expiringSoon} in 90 days
              </Caption>
            </View>
          )}
        </View>
      </Card>
    </Pressable>
  );
}

